import React, { useEffect, useRef, useState } from "react";

/**
 * Count-up animation. Targets a number value and animates from 0 (or previous)
 * with an easing curve so the demo feels alive — perfect for KPIs.
 *
 * Props:
 *  - value: target number (required)
 *  - duration: ms (default 1100)
 *  - format: function(n) => string ; default = toLocaleString()
 *  - prefix / suffix: prepended / appended
 *  - className: pass-through
 */
export default function CountUp({
  value = 0,
  duration = 1100,
  format,
  prefix = "",
  suffix = "",
  className = "",
}) {
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  const startedAt = useRef(null);
  const raf = useRef(null);

  useEffect(() => {
    if (value == null || Number.isNaN(value)) return;
    fromRef.current = display;
    startedAt.current = null;

    const step = (ts) => {
      if (!startedAt.current) startedAt.current = ts;
      const elapsed = ts - startedAt.current;
      const t = Math.min(1, elapsed / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      const current = fromRef.current + (value - fromRef.current) * eased;
      setDisplay(current);
      if (t < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => raf.current && cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  const formatted = format ? format(display) : Math.round(display).toLocaleString();
  return (
    <span className={className}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
